<template>
  <div
    ref="containerElement"
    class="swipeable"
    :class="{
      'swipeable--hide-gradient': hideGradient,
      'swipeable--is-at-left': currentX >= 0,
      'swipeable--is-at-right': currentX <= minX,
    }"
  >
    <!-- TODO: remove gradient on hover -->
    <div
      class="swipeable__gradient swipeable__gradient--before"
      v-if="!hideGradient"
    ></div>
    <div ref="contentElement" class="swipeable__content">
      <suspense @resolve="onResolve">
        <slot />
      </suspense>
    </div>
    <div
      class="swipeable__gradient swipeable__gradient--after"
      v-if="!hideGradient"
    ></div>
  </div>
</template>

<script setup>
import { ref, toRefs, watch, onBeforeUnmount, computed, nextTick } from 'vue';
import { useStore } from 'vuex';
import { animationFrame } from '@/shared/js/utils/animation';
import { gsap } from '@gsap/shockingly';
import { Draggable } from '@gsap/shockingly/Draggable';
import { useMounted } from '@vueuse/core';

const props = defineProps({
  hideGradient: {
    type: Boolean,
    default: false,
  },
  snap: {
    type: [Boolean, Function],
    default: false,
  },
  type: {
    type: String,
    default: 'x',
  },
  x: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(['init', 'update:x']);

const { snap, type } = toRefs(props);
const store = useStore();

const contentElement = ref(undefined);
const containerElement = ref(undefined);

const minX = ref(0);
const draggable = ref(undefined);

const isMounted = useMounted();
const hasResolved = ref(false);
const onResolve = () => {
  hasResolved.value = true;
};
const isReady = computed(() => isMounted.value && hasResolved.value);

const currentX = computed({
  get() {
    return props.x;
  },
  set(value) {
    emit('update:x', value);
  },
});

watch(isReady, async isReady => {
  await nextTick();

  if (!isReady) return;

  if (!contentElement.value) {
    console.error('contentElement was not set, when `isReady` was called');
    return;
  }

  minX.value =
    contentElement.value.offsetWidth - contentElement.value.scrollWidth;

  draggable.value = Draggable.create(contentElement.value, {
    type: type.value,
    inertia: true,
    snap: snap.value,
    // we need no bounds when using scrollLeft
    bounds: { minX: minX.value, maxX: 0 },
    // when `type` is `scrolLeft` we want no overdragging, because it
    // disturbs elements which are positioned sticky
    // edgeResistance: type.value === 'scrollLeft' ? 0.9 : 0.75,
    minimumMovement: 6,
    onThrowComplete() {
      currentX.value = this.x;
    },
    onThrowUpdate() {
      currentX.value = this.x;
    },
    onDrag() {
      currentX.value = this.x;
    },
    onDragEnd() {
      currentX.value = this.x;
    },
  });

  await animationFrame();

  updateBounds();

  emit('init');
});

const updateBounds = () => {
  if (!contentElement.value) {
    return console.warn(
      'contentElement was not set, when `updateBounds` was called'
    );
  }

  if (!draggable.value[0]) {
    return console.warn(
      'draggable was not set, when `updateBounds` was called'
    );
  }

  minX.value =
    contentElement.value.offsetWidth - contentElement.value.scrollWidth;

  draggable.value[0].applyBounds({ minX: minX.value, maxX: 0 });
  draggable.value[0].update();
};

const innerWidth = computed(() => store.getters['window/innerWidth']);

watch(innerWidth, () => {
  updateBounds();

  if (type.value === 'scrollLeft') return;

  const clamped = gsap.utils.clamp(minX.value, 0)(draggable.value[0].x);
  gsap.quickSetter(contentElement.value, 'x', 'px')(clamped);
  currentX.value = clamped;
});

onBeforeUnmount(() => {
  // TODO unbindEvents();
});
</script>
